import { resetListViewExpansionViewState } from 'owa-mail-actions/lib/mailListSelectionActions';
import { focusJumpToRowMutator, MailRowDataPropertyGetter } from 'owa-mail-list-store';
import type { TableView } from 'owa-mail-list-store';
import {
    createSubjectCache,
    findImportanceJumpToIndex,
    findNameJumpToIndex,
    findSubjectJumpToIndex,
    isCategorySearchSortOrder,
    isImportanceSearchSortOrder,
    isRecipientSearchSortOrder,
    isSubjectSearchSortOrder,
    type ClientSearchSortOrder,
} from 'owa-mail-list-search';
import findCategoryJumpToIndex from 'owa-mail-list-search/lib/utils/findCategoryJumpToIndex';
import { getCategoryForCategorySortByRowKey } from 'owa-mail-category-sort';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { expandGroupHeaderContainingRow } from 'owa-virtualized-group-headers';
import getNormalizedSubject from 'owa-mail-compose-actions/lib/utils/getNormalizedSubject';

/**
 * Jump To for a table whose rows are all loaded and sorted on the client: focuses the first row
 * at or past the matching string, and the list scrolls it to the top. Selection is left as is.
 */
export default function executeClientJumpTo(
    tableView: TableView,
    matchingString: string,
    clientSortOrder: ClientSearchSortOrder
): void {
    const index = isSubjectSearchSortOrder(clientSortOrder)
        ? findSubjectJumpToIndex(
              tableView.rowKeys,
              getRowNormalizedSubject(tableView),
              clientSortOrder === 'SubjectDescending',
              matchingString
          )
        : isImportanceSearchSortOrder(clientSortOrder)
          ? findImportanceJumpToIndex(
                tableView.rowKeys,
                rowKey => MailRowDataPropertyGetter.getImportance(rowKey, tableView),
                matchingString
            )
          : isCategorySearchSortOrder(clientSortOrder)
            ? findCategoryJumpToIndex(
                  tableView.rowKeys,
                  rowKey => getCategoryForCategorySortByRowKey(getMailboxInfo(tableView), rowKey),
                  clientSortOrder === 'CategoryDescending',
                  matchingString
              )
            : findNameJumpToIndex(
                  tableView.rowKeys,
                  rowKey =>
                      isRecipientSearchSortOrder(clientSortOrder)
                          ? MailRowDataPropertyGetter.getUniqueRecipients(rowKey, tableView)?.[0]
                          : MailRowDataPropertyGetter.getUniqueSenders(rowKey, tableView)?.[0],
                  clientSortOrder,
                  matchingString
              );
    if (index < 0) {
        return;
    }

    resetListViewExpansionViewState();
    const rowKey = tableView.rowKeys[index];
    expandGroupHeaderContainingRow(rowKey, tableView);
    focusJumpToRowMutator(tableView, rowKey);
}

function getRowNormalizedSubject(tableView: TableView): (rowKey: string) => string {
    const getSubject = createSubjectCache(getNormalizedSubject);
    return rowKey => getSubject(MailRowDataPropertyGetter.getSubject(rowKey, tableView));
}
